import React, { SyntheticEvent, useEffect, useState, ChangeEvent } from 'react';
import { useParams, useNavigate, NavLink, Link } from 'react-router-dom';
import axios from 'axios';
import {Typography,Button,Box,Card,Divider,Grid,TextField,FormControl,InputLabel,Select,MenuItem,CardContent,CardActions,SelectChangeEvent,} from '@mui/material';
import Title from '../../../components/Title';
import { Type_Operation } from '../../../models/type_operation';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface Expense {
  id: number;
  name: string;
  type_expense: {
    id: number;
    name: string;
  };
  type_operation: {
    id: number;
    name: string;
  };
  type_state: {
    id: number;
    name: string;
  };
}

interface FormState {
  name: string;
  type_expense_id: string;
  type_operation_id: string;
}

const TipoGastoEdit: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = 'Administrar Tipo Gastos';
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Estados
  const RESTA_OPERATION_ID = '2';
  const [formState, setFormState] = useState<FormState>({
    name: '',
    type_expense_id: '',
    type_operation_id: '',
  });
  const [operations, setOperations] = useState<Type_Operation[]>([]);
  const [isModified, setIsModified] = useState(false);

  const isCobroOrOficina =
    formState.type_expense_id === '1' || formState.type_expense_id === '2';

  const operationsFiltered = isCobroOrOficina
    ? operations.filter(op => op.id.toString() === RESTA_OPERATION_ID)
    : operations;

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const [{ data: operationData }, { data: expenseData }] = await Promise.all([
          axios.get('typeoperation'),
          axios.get<Expense>(`expenses/${id}`),
        ]);

        setOperations(operationData.data);
        setFormState({
          name: expenseData.name,
          type_expense_id: expenseData.type_expense.id.toString(),
          type_operation_id: expenseData.type_operation.id.toString(),
        });
      } catch (error) {
        showAlert('Error al cargar los datos. Por favor, intente nuevamente.', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [id]);

  // Forzar "resta" si el tipo es Cobro/Oficina
  useEffect(() => {
    if (!isCobroOrOficina) return;
    setFormState(prev => {
      if (prev.type_operation_id === RESTA_OPERATION_ID) return prev;
      setIsModified(true);
      return { ...prev, type_operation_id: RESTA_OPERATION_ID };
    });
  }, [isCobroOrOficina, RESTA_OPERATION_ID]);

  // Manejar cambios en los campos del formulario
  const handleInputChange = (field: keyof FormState) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setIsModified(true);
  };

  const handleSelectChange = (field: keyof FormState) => (
    event: SelectChangeEvent<unknown>
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event.target.value as string,
    }));
    setIsModified(true);
  };

  // Manejar el envío del formulario
  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!formState.name || !formState.type_expense_id || !formState.type_operation_id) {
      showAlert('Tiene campos pendientes por diligenciar!', 'warning');
      return;
    }

    try {
      showLoading();
      await axios.put(`expenses/${id}`, {
        name: formState.name,
        type_expense_id: formState.type_expense_id,
        type_operation_id: formState.type_operation_id,
      });
      showAlert('Se ha realizado la actualización exitosamente!', 'success');
      setTimeout(() => navigate('/administrar/tipogasto'), 1000);
    } catch (error) {
      showAlert('Error al actualizar el gasto. Por favor, intente nuevamente.', 'error');
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Editar Información Gastos</Title>
      </Box>

      <Card>
        <Divider sx={{ margin: 0 }} />
        <form onSubmit={handleSubmit}>
          <CardContent>
            <Grid container spacing={2}>


              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Descripción"
                  size='small'
                  placeholder="Descripción"
                  value={formState.name}
                  onChange={handleInputChange('name')}
                  inputProps={{ maxLength: 30 }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Seleccionar Tipo de Gasto</InputLabel>
                  <Select
                    value={formState.type_expense_id}
                    size="small"
                    onChange={handleSelectChange('type_expense_id')}
                    label="Seleccionar Tipo de Gasto"
                    required
                  >
                    <MenuItem value="1">Cobro</MenuItem>
                    <MenuItem value="2">Oficina</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="operation">Seleccionar Tipo Operación</InputLabel>
                  <Select
                    label="Operación"
                    size="small"
                    value={formState.type_operation_id}
                    onChange={handleSelectChange('type_operation_id')}
                    labelId="operation"
                    required
                    disabled={isCobroOrOficina}
                  >
                    {(operationsFiltered.length > 0 ? operationsFiltered : operations).map((op) => (
                      <MenuItem key={op.id} value={op.id.toString()}>
                        {op.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider sx={{ margin: 0 }} />
          <CardActions>
            <Button
              size='small'
              type="submit"
              sx={{ mr: 2 }}
              variant="contained"
              disabled={!isModified}
            >
              Guardar
            </Button>
            <Link to="/administrar/tipogasto">
              <Button size='small' color="secondary" variant="outlined">
                Cancelar
              </Button>
            </Link>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default TipoGastoEdit;